import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Plus, Search, FileText, Download, Eye } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const LegalLibrary = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/library/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المكتبة' : 'Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'contract', name_ar: 'العقود', name_en: 'Contracts', count: 15 },
    { id: 'memo', name_ar: 'المذكرات', name_en: 'Memos', count: 25 },
    { id: 'petition', name_ar: 'صحف الدعاوى', name_en: 'Petitions', count: 20 },
    { id: 'response', name_ar: 'الردود', name_en: 'Responses', count: 18 },
    { id: 'judgment', name_ar: 'الأحكام', name_en: 'Judgments', count: 12 },
  ];

  const sampleTemplates = [
    { id: '1', title_ar: 'عقد عمل نموذجي', title_en: 'Employment Contract', category: 'contract' },
    { id: '2', title_ar: 'مذكرة دفاع', title_en: 'Defense Memo', category: 'memo' },
    { id: '3', title_ar: 'صحيفة دعوى مدنية', title_en: 'Civil Lawsuit', category: 'petition' },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'المكتبة القانونية' : 'Legal Library'}</h1>
              <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'النماذج والصيغ القانونية' : 'Legal forms and templates'}</p>
            </div>
            <Button className="btn-primary gap-2">
              <Plus className="w-5 h-5" />
              {i18n.language === 'ar' ? 'إضافة قالب' : 'Add Template'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {categories.map((cat) => (
              <Card key={cat.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: '#9D7E4F' }} />
                    <h3 className="font-bold mb-2">{i18n.language === 'ar' ? cat.name_ar : cat.name_en}</h3>
                    <Badge>{cat.count}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث في المكتبة...' : 'Search library...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sampleTemplates.map((template) => (
                  <div key={template.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: '#9D7E4F' }}>
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{i18n.language === 'ar' ? template.title_ar : template.title_en}</p>
                        <p className="text-sm text-text-secondary">{template.category}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline"><Download className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalLibrary;